import {
  CATALOG_EMBED_RUNTIME_ATTR_KEYS,
  type CompiledTemplateJson,
  isCatalogEmbedNodeType,
  resolveEntityTemplate,
} from "@repo/shared";
import { isEqual } from "lodash-es";
import { isMeaningfulAtomNodeType } from "@/features/rich-text/meaningful-atom-types";

export type VersionChangeKind = "added" | "removed" | "changed";
export type VersionFieldType = "simple" | "richText";

/**
 * What kind of value a simple field holds — which decides whether comparing two of its values
 * character by character means anything.
 *
 * `text` is typed prose (a name, a free-text attribute): its two versions differ by an edit, and a
 * token diff is the shortest way to say what the edit was. `option` is one of a fixed set of
 * choices and `owner` is a person; neither was *typed*, so marking "S~~ource~~" against
 * "S<ins>erving</ins>" invents an edit that never happened and makes two unrelated choices look
 * like one mistyped word (LAT-905 UAT round 4). Those are shown whole, on each side.
 *
 * Absent means `text`: rich-text fields have no use for it, and a field nobody classified is
 * treated as prose, which is the reading every tracked field had before this existed.
 */
export type VersionTrackedValueKind = "text" | "option" | "owner";

export interface VersionChange {
  fieldKey: string;
  fieldLabel: string;
  fieldType: VersionFieldType;
  changeKind: VersionChangeKind;
  fromDisplay?: string;
  toDisplay?: string;
}

export interface VersionTrackedField {
  fieldKey: string;
  fieldLabel: string;
  fieldType: VersionFieldType;
  /**
   * The label is a stand-in derived from something coarser than the field itself (today: a tab
   * label standing in for the whole tab document). A compiled-template label for the same key
   * replaces it; a label without this flag is authoritative and is kept.
   */
  labelIsFallback?: boolean;
  /** How the field's value should be read to a person; absent is `text`. */
  valueKind?: VersionTrackedValueKind;
  getValue?: (snapshot: Record<string, unknown>) => unknown;
  formatDisplay?: (value: unknown) => string | undefined;
}

/** The field's value kind with the default applied — the one place `undefined` means `text`. */
export function resolveVersionTrackedValueKind(
  field: Pick<VersionTrackedField, "valueKind"> | null | undefined
): VersionTrackedValueKind {
  return field?.valueKind ?? "text";
}

type StoredDocumentNode = {
  type?: unknown;
  attrs?: Record<string, unknown>;
  content?: unknown;
  marks?: unknown;
  text?: unknown;
};

const RUNTIME_ATTR_KEYS = new Set([
  "blockId",
  "_diffStatus",
  "_diffOldValue",
  "required",
  "guidance",
  "assistantGuidance",
  "expectedMentionTypes",
  "mentionSuggestionChar",
]);

const CATALOG_RUNTIME_ATTR_KEYS = new Set<string>(CATALOG_EMBED_RUNTIME_ATTR_KEYS);

const RUNTIME_MARK_TYPES = new Set([
  "clarificationAnchor",
  "diffInsert",
  "diffDelete",
  "diffFormat",
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isStoredDocumentNode(value: unknown): value is StoredDocumentNode {
  return isRecord(value);
}

function materializeStoredDocumentNode(value: unknown): StoredDocumentNode | null {
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value) as unknown;
      return isStoredDocumentNode(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }
  return isStoredDocumentNode(value) ? value : null;
}

function documentNodeHasMeaningfulContent(node: StoredDocumentNode): boolean {
  const type = typeof node.type === "string" ? node.type : "";
  if (type === "text") {
    const text = node.attrs?.text ?? (node as StoredDocumentNode & { text?: unknown }).text;
    return typeof text === "string" && text.trim().length > 0;
  }

  if (isMeaningfulAtomNodeType(type)) {
    return true;
  }

  if (type === "atomicField") {
    return hasMeaningfulValue(node.attrs?.value);
  }

  if (Array.isArray(node.content)) {
    return node.content.some(
      (child) => isStoredDocumentNode(child) && documentNodeHasMeaningfulContent(child)
    );
  }

  return false;
}

function getVersionDocumentFieldValue(
  snapshot: Record<string, unknown>,
  fieldKey: string
): unknown {
  const documentJson = snapshot.documentJson;
  if (!isRecord(documentJson)) {
    return undefined;
  }

  for (const value of Object.values(documentJson)) {
    const tabDocument = materializeStoredDocumentNode(value);
    if (!tabDocument || !Array.isArray(tabDocument.content)) {
      continue;
    }

    const fieldNode = tabDocument.content.find(
      (node) => isStoredDocumentNode(node) && node.attrs?.fieldId === fieldKey
    );
    if (!isStoredDocumentNode(fieldNode)) {
      continue;
    }

    if (Array.isArray(fieldNode.content)) {
      return fieldNode.content;
    }

    if (fieldNode.type === "atomicField") {
      return fieldNode.attrs?.value ?? "";
    }

    return fieldNode.attrs ?? null;
  }

  return undefined;
}

function toVersionFieldType(nodeType: string): VersionFieldType {
  return nodeType === "atomicField" ? "simple" : "richText";
}

export function buildCompiledTemplateVersionTrackedFields({
  baseFields,
  frameworkEntityTypeKey,
  compiledTemplateJson,
}: {
  baseFields: VersionTrackedField[];
  frameworkEntityTypeKey: string;
  compiledTemplateJson?: CompiledTemplateJson | null;
}): VersionTrackedField[] {
  const template = resolveEntityTemplate(compiledTemplateJson, frameworkEntityTypeKey);
  if (!template) {
    return baseFields;
  }

  const trackedFields = [...baseFields];
  const trackedFieldIndexes = new Map(
    trackedFields.map((field, index) => [field.fieldKey, index] as const)
  );

  for (const tab of template.tabs) {
    for (const field of tab.fields) {
      // No `valueKind`: a template's atomic field is typed into the document, so it reads as text.
      // A base field the template widens keeps its own classification through the spread below —
      // an attribute the catalog knows is a choice does not become prose by also being templated.
      const compiledField: VersionTrackedField = {
        fieldKey: field.fieldId,
        fieldLabel: field.label,
        fieldType: toVersionFieldType(field.nodeType),
        getValue: (snapshot) => getVersionDocumentFieldValue(snapshot, field.fieldId),
      };
      const existingIndex = trackedFieldIndexes.get(field.fieldId);

      if (existingIndex !== undefined) {
        const existingField = trackedFields[existingIndex];
        if (!existingField || existingField.getValue) {
          continue;
        }

        const compiledLabelWins =
          existingField.labelIsFallback === true ||
          existingField.fieldLabel === existingField.fieldKey;

        trackedFields[existingIndex] = {
          ...existingField,
          fieldLabel: compiledLabelWins ? compiledField.fieldLabel : existingField.fieldLabel,
          labelIsFallback: false,
          fieldType: compiledField.fieldType,
          getValue: compiledField.getValue,
        };
        continue;
      }

      trackedFieldIndexes.set(field.fieldId, trackedFields.length);
      trackedFields.push(compiledField);
    }
  }

  return trackedFields;
}

function hasMeaningfulValue(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.some(hasMeaningfulValue);
  if (isStoredDocumentNode(value) && typeof value.type === "string") {
    return documentNodeHasMeaningfulContent(value);
  }
  return true;
}

function defaultFormatDisplay(value: unknown): string | undefined {
  if (!hasMeaningfulValue(value)) return undefined;

  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length > 80 ? `${trimmed.slice(0, 77)}...` : trimmed;
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return undefined;
    if (value.every((entry) => typeof entry === "string")) {
      const joined = value.join(", ");
      return joined.length > 80 ? `${value.length} items` : joined;
    }
    return `${value.length} items`;
  }

  return "Updated";
}

/**
 * The raw value a tracked field holds in a snapshot.
 *
 * Exported because the override reconciliation dialog (LAT-905) has to read the same field out of
 * two *other* snapshots — the version a save overrode and the version it produced — and reading it
 * any other way would drift from what the version history says changed.
 */
export function getVersionTrackedFieldValue(
  field: VersionTrackedField,
  snapshot: Record<string, unknown>
): unknown {
  if (field.getValue) {
    return field.getValue(snapshot);
  }

  return snapshot[field.fieldKey];
}

/**
 * The human-readable form of a tracked field's value — the field's own formatter when it has one
 * (enum labels), the generic one otherwise. Exported for the same reason as the reader above.
 */
export function formatVersionTrackedFieldDisplay(
  field: VersionTrackedField,
  value: unknown
): string | undefined {
  return field.formatDisplay ? field.formatDisplay(value) : defaultFormatDisplay(value);
}

function canonicalizeRichTextAttrs(
  attrs: Record<string, unknown> | undefined,
  nodeType: string | undefined
): Record<string, unknown> | undefined {
  if (!attrs) return undefined;

  const nextAttrs: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(attrs)) {
    if (value === null || value === undefined) continue;
    if (RUNTIME_ATTR_KEYS.has(key)) continue;
    if (isCatalogEmbedNodeType(nodeType ?? "") && CATALOG_RUNTIME_ATTR_KEYS.has(key)) continue;
    if (nodeType === "orderedList" && key === "start" && value === 1) continue;
    nextAttrs[key] = value;
  }

  return Object.keys(nextAttrs).length > 0 ? nextAttrs : undefined;
}

function canonicalizeRichTextValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map((entry) => canonicalizeRichTextValue(entry));
  }

  if (!isRecord(value)) {
    return value;
  }

  const nodeType = typeof value.type === "string" ? value.type : undefined;
  const nextValue: Record<string, unknown> = {};

  if (value.type !== undefined) {
    nextValue.type = value.type;
  }

  if (typeof value.text === "string") {
    nextValue.text = value.text;
  }

  const attrs = canonicalizeRichTextAttrs(
    value.attrs as Record<string, unknown> | undefined,
    nodeType
  );
  if (attrs) {
    nextValue.attrs = attrs;
  }

  if (Array.isArray(value.marks)) {
    const marks = value.marks
      .map((mark) => canonicalizeRichTextValue(mark))
      .filter((mark) => {
        if (!isRecord(mark)) return true;
        return typeof mark.type !== "string" || !RUNTIME_MARK_TYPES.has(mark.type);
      });
    if (marks.length > 0) {
      nextValue.marks = marks;
    }
  }

  if (Array.isArray(value.content)) {
    nextValue.content = value.content.map((child) => canonicalizeRichTextValue(child));
  }

  return nextValue;
}

function getComparableFieldValue(field: VersionTrackedField, value: unknown): unknown {
  return field.fieldType === "richText" ? canonicalizeRichTextValue(value) : value;
}

export function buildVersionChanges({
  previousSnapshot,
  nextSnapshot,
  trackedFields,
}: {
  previousSnapshot: Record<string, unknown>;
  nextSnapshot: Record<string, unknown>;
  trackedFields: VersionTrackedField[];
}): VersionChange[] {
  const changes: VersionChange[] = [];

  for (const field of trackedFields) {
    const previousValue = getComparableFieldValue(
      field,
      getVersionTrackedFieldValue(field, previousSnapshot)
    );
    const nextValue = getComparableFieldValue(
      field,
      getVersionTrackedFieldValue(field, nextSnapshot)
    );

    if (isEqual(previousValue, nextValue)) {
      continue;
    }

    const previousHasValue = hasMeaningfulValue(previousValue);
    const nextHasValue = hasMeaningfulValue(nextValue);

    if (!previousHasValue && !nextHasValue) {
      continue;
    }

    const changeKind: VersionChangeKind =
      !previousHasValue && nextHasValue
        ? "added"
        : previousHasValue && !nextHasValue
          ? "removed"
          : "changed";

    if (field.fieldType === "richText") {
      changes.push({
        fieldKey: field.fieldKey,
        fieldLabel: field.fieldLabel,
        fieldType: field.fieldType,
        changeKind,
      });
      continue;
    }

    changes.push({
      fieldKey: field.fieldKey,
      fieldLabel: field.fieldLabel,
      fieldType: field.fieldType,
      changeKind,
      fromDisplay: formatVersionTrackedFieldDisplay(field, previousValue),
      toDisplay: formatVersionTrackedFieldDisplay(field, nextValue),
    });
  }

  return changes;
}

function joinLabels(labels: string[], options?: { compactTail?: boolean }): string {
  if (labels.length === 0) return "";
  if (labels.length === 1) return labels[0]!;

  if (options?.compactTail) {
    return labels.join(", ");
  }

  if (labels.length === 2) {
    return `${labels[0]} and ${labels[1]}`;
  }

  const lastLabel = labels[labels.length - 1];
  return `${labels.slice(0, -1).join(", ")}, and ${lastLabel}`;
}

function buildGroupedChangePhrase(
  kind: VersionChangeKind,
  labels: string[],
  options?: { compactTail?: boolean }
): string {
  const action = kind === "added" ? "added" : kind === "removed" ? "removed" : "changed";
  return `${joinLabels(labels, options)} ${action}`;
}

function isStandaloneSummaryChange(change: VersionChange): boolean {
  return change.fieldKey === "projectSnapshotRestore";
}

function isInitialVersionChange(change: VersionChange): boolean {
  return (
    change.fieldLabel === "Entity" &&
    (change.changeKind === "added" || change.changeKind === "changed")
  );
}

export function buildVersionSummary(
  changes: VersionChange[],
  options?: { maxVisible?: number; fallback?: string }
): string {
  if (changes.length === 0) {
    return options?.fallback ?? "No changes";
  }

  if (changes.length === 1 && isInitialVersionChange(changes[0]!)) {
    return "Initial version";
  }

  const maxVisible = options?.maxVisible ?? 4;
  const visibleCount = changes.length - maxVisible === 1 ? maxVisible + 1 : maxVisible;
  const visibleChanges = changes.slice(0, visibleCount);
  const remainingCount = changes.length - visibleChanges.length;
  const standalonePhrases = visibleChanges
    .filter(isStandaloneSummaryChange)
    .map((change) => change.fieldLabel);
  const groupedChanges = visibleChanges.filter((change) => !isStandaloneSummaryChange(change));

  const groupedLabelsByKind = new Map<VersionChangeKind, string[]>();
  const kindOrder: VersionChangeKind[] = [];

  for (const change of groupedChanges) {
    const labels = groupedLabelsByKind.get(change.changeKind);
    if (labels) {
      labels.push(change.fieldLabel);
      continue;
    }

    groupedLabelsByKind.set(change.changeKind, [change.fieldLabel]);
    kindOrder.push(change.changeKind);
  }

  const phrases = kindOrder.map((kind) =>
    buildGroupedChangePhrase(kind, groupedLabelsByKind.get(kind) ?? [], {
      compactTail: remainingCount > 0,
    })
  );
  const summarySegments = [...standalonePhrases, ...phrases];

  if (remainingCount <= 0) {
    return summarySegments.join(". ");
  }

  const groupedSummary =
    phrases.length > 0
      ? `${phrases.join(", ")}, and ${remainingCount} more`
      : `${remainingCount} more`;

  if (standalonePhrases.length === 0) {
    return groupedSummary;
  }

  return [...standalonePhrases, groupedSummary].join(". ");
}

export function createGenericVersionChange(
  fieldKey: string,
  fieldLabel: string,
  changeKind: VersionChangeKind = "changed"
): VersionChange {
  return {
    fieldKey,
    fieldLabel,
    fieldType: "simple",
    changeKind,
  };
}
